/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { faEdit, faExchangeAlt, faRecycle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { Button } from '@mui/material';
import cs from 'classnames';
import { useUnit } from 'effector-react';
import { type MRT_ColumnDef } from 'material-react-table';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { DeleteDialog } from './components/DeleteDialog';
import { StrategyDialog } from './components/StrategyDialog';
import { useStrategies } from './hooks';
import { ProtectedComponent } from '../../../components/ProtectedComponent';
import { TableGridLocal } from '../../../components/TableGridLocal';
import { User$ } from '../../../effector/user';
import { AggregateStrategy } from '../../../types/aggregateStrategy';
import { UserRoles } from '../../../types/userRoles';
import { boolToString } from '../../../utils';
import { UserAction, checkPermissions } from '../../../utils/permissions';
import {
  PAGE_ADMINISTRATION,
  PAGE_ADMINISTRATION_STRATEGIES,
  PAGE_ADMINISTRATION_STRATEGY_DETAILS,
} from '../../Routes';
import { RecalculationDialog } from './components/RecalculationDialog';
import { SplitDialog } from './components/SplitDialog';
import { VisibilityDialog } from './components/VisibilityDialog';

export const Strategies: React.FC = () => {
  const [openStrategyDialog, setOpenStrategyDialog] = useState(false);
  const [editingStrategyId, setEditingStrategyId] = useState<string | null>(null);
  const [openRecalculationDialog, setOpenRecalculationDialog] = useState(false);
  const [openSplitDialog, setOpenSplitDialog] = useState(false);
  const [changeVisibilityCandidate, setChangeVisibilityCandidate] = useState<{
    strategyId: string;
    isOpen: boolean;
  } | null>(null);
  const [deleteCandidate, setDeleteCandidate] = useState<{
    strategyId: string;
    active: boolean;
  } | null>(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const user = useUnit(User$);

  const { data, refetch } = useStrategies();

  const handleAddStrategy = useCallback(() => {
    setOpenStrategyDialog(true);
  }, []);
  const handleRecalculateDialog = useCallback(() => {
    setOpenRecalculationDialog(true);
  }, []);
  const handleSplitDialog = useCallback(() => {
    setOpenSplitDialog(true);
  }, []);
  const handleCloseDialog = useCallback(() => {
    setOpenStrategyDialog(false);
    setEditingStrategyId(null);
    setOpenRecalculationDialog(false);
    setOpenSplitDialog(false);
    setChangeVisibilityCandidate(null);
    setDeleteCandidate(null);
  }, []);

  const handleUpdate = useCallback(() => {
    handleCloseDialog();
    refetch();
  }, [handleCloseDialog, refetch]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const editId = params.get('editId');

    setEditingStrategyId(editId);
  }, [location.search]);

  const isDeveloper = user?.role && user.role === UserRoles.Developer;

  const strategies = useMemo(() => data?.result || [], [data]);

  const currencies = useMemo(
    () =>
      strategies
        .map(({ currency }) => currency)
        .filter((value, index, array) => {
          return array.indexOf(value) === index;
        }),
    [strategies],
  );

  const columns = useMemo<MRT_ColumnDef<AggregateStrategy>[]>(() => {
    if (user) {
      const items: (MRT_ColumnDef<AggregateStrategy> & { action: UserAction })[] = [
        {
          accessorKey: 'name',
          header: 'Название',
          filterVariant: 'text',
          action: UserAction.administrationStrategiesAction,
          Cell: ({ cell }) => (
            <Link to={`${PAGE_ADMINISTRATION}/${PAGE_ADMINISTRATION_STRATEGY_DETAILS}/${cell.row.original.id}`}>
              {cell.getValue() as string}
            </Link>
          ),
        },
        {
          accessorFn: (originalRow) => new Date(originalRow.creationDate),
          id: 'creationDate',
          header: 'Создана',
          filterVariant: 'date-range',
          action: UserAction.administrationStrategiesAction,
          Cell: ({ cell }) => {
            const timeFormatted = moment(cell.row.original.creationDate).format('DD.MM.YYYY HH:mm');
            return <span>{timeFormatted}</span>;
          },
        },
        {
          accessorKey: 'leverage',
          header: 'Плечо',
          filterVariant: 'range',
          filterFn: 'betweenInclusive',
          action: UserAction.administrationStrategiesAction,
          size: 50,
        },
        {
          accessorKey: 'currency',
          header: 'Валюта',
          filterVariant: 'multi-select',
          filterSelectOptions: currencies,
          action: UserAction.administrationStrategiesAction,
          size: 50,
        },
        {
          accessorFn: (originalRow) => boolToString(originalRow.open),
          header: 'Запущена',
          filterVariant: 'select',
          filterSelectOptions: ['Да', 'Нет'],
          action: UserAction.administrationStrategiesAction,
          size: 50,
          Cell: ({ cell }) => boolToString(cell.row.original.open),
        },
        {
          accessorFn: (originalRow) => boolToString(originalRow.isRestricted, { yes: 'Закрытая', no: 'Открытая' }),
          header: 'Видимость',
          filterVariant: 'select',
          filterSelectOptions: ['Закрытая', 'Открытая'],
          action: UserAction.administrationStrategiesAction,
          Cell: ({ cell }) => boolToString(cell.row.original.isRestricted, { yes: 'Закрытая', no: 'Открытая' }),
        },
        {
          accessorFn: (originalRow) => boolToString(originalRow.isBlogger),
          header: 'Стратегия блогера',
          filterVariant: 'select',
          filterSelectOptions: ['Да', 'Нет'],
          action: UserAction.administrationStrategiesAction,
          size: 50,
          Cell: ({ cell }) => boolToString(cell.row.original.isBlogger),
        },
        {
          accessorKey: 'edit',
          header: '',
          enableColumnFilter: false,
          enableColumnOrdering: false,
          enableColumnFilterModes: false,
          enableColumnActions: false,
          enableSorting: false,
          size: 20,
          action: UserAction.administrationStrategiesAction,
          Cell: ({ cell }) => {
            return (
              <span className="pointer">
                <a
                  href={`${PAGE_ADMINISTRATION}/${PAGE_ADMINISTRATION_STRATEGIES}?editId=${cell.row.original.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FontAwesomeIcon
                    icon={faEdit}
                    title="Редактировать"
                    className={cs({ 'fa-disabled': isDeveloper && !cell.row.original.testMode })}
                    onClick={(event) => {
                      if (isDeveloper && !cell.row.original.testMode) {
                        event.preventDefault();
                      }
                    }}
                  />
                </a>
              </span>
            );
          },
        },
        {
          accessorKey: 'close',
          header: '',
          enableColumnFilter: false,
          enableColumnOrdering: false,
          enableColumnFilterModes: false,
          enableColumnActions: false,
          enableSorting: false,
          size: 40,
          action: UserAction.administrationStrategiesAction,
          Cell: ({ cell }) => {
            return (
              <span className="pointer">
                <FontAwesomeIcon
                  icon={faExchangeAlt}
                  title="Запустить/Убрать"
                  className={cs({
                    fall: cell.row.original.testMode || !isDeveloper,
                    'fa-disabled': isDeveloper && !cell.row.original.testMode,
                  })}
                  onClick={() => {
                    setChangeVisibilityCandidate({
                      strategyId: cell.row.original.id,
                      isOpen: cell.row.original.open,
                    });
                  }}
                />
              </span>
            );
          },
        },
        {
          accessorKey: 'remove',
          header: '',
          enableColumnFilter: false,
          enableColumnOrdering: false,
          enableColumnFilterModes: false,
          enableColumnActions: false,
          enableSorting: false,
          size: 40,
          action: UserAction.administrationStrategiesAction,
          Cell: ({ cell }) => {
            return (
              <span className="pointer">
                <FontAwesomeIcon
                  icon={faRecycle}
                  title="Удалить"
                  className={cs({
                    'fa-disabled': (isDeveloper && !cell.row.original.testMode) || cell.row.original.open,
                  })}
                  onClick={() => {
                    setDeleteCandidate({
                      strategyId: cell.row.original.id,
                      active: cell.row.original.active,
                    });
                  }}
                />
              </span>
            );
          },
        },
      ];

      const filteredColumns = items.filter((item) => checkPermissions(user.role, item.action as UserAction));
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      return filteredColumns.map(({ action, ...rest }) => rest);
    }
    return [];
  }, [currencies, user, isDeveloper]);

  const columnsFilters = useMemo(() => {
    if (!location.search) {
      return [];
    }

    const params = new URLSearchParams(location.search);
    const filtersParam = params.get('filters');

    if (filtersParam) {
      try {
        return JSON.parse(decodeURIComponent(filtersParam));
      } catch {
        return [];
      }
    }

    // Backward compatibility: previous implementation stored raw JSON in the search string.
    let rawParams = location.search;
    if (rawParams.startsWith('?')) {
      rawParams = rawParams.substring(1);
    }

    if (!rawParams || rawParams.includes('=')) {
      return [];
    }

    try {
      return JSON.parse(decodeURI(rawParams));
    } catch {
      return [];
    }
  }, [location.search]);

  const handleEditDialogClose = useCallback(() => {
    handleCloseDialog();

    const params = new URLSearchParams(location.search);

    if (params.has('editId')) {
      params.delete('editId');

      const nextSearch = params.toString();

      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : '',
        },
        { replace: true },
      );
    }
  }, [handleCloseDialog, location.pathname, location.search, navigate]);

  const handleEditDialogUpdate = useCallback(() => {
    handleUpdate();

    const params = new URLSearchParams(location.search);

    if (params.has('editId')) {
      params.delete('editId');

      const nextSearch = params.toString();

      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : '',
        },
        { replace: true },
      );
    }
  }, [handleUpdate, location.pathname, location.search, navigate]);

  return (
    <>
      <ProtectedComponent userAction={UserAction.administrationDeveloper}>
        <div className="form__group">
          <br />
          <p>Для данного пользователя доступно редактирование только тестовых стратегий.</p>
          <br />
        </div>
      </ProtectedComponent>

      <div className="form__group">
        <ProtectedComponent userAction={UserAction.administrationStrategiesAction}>
          <Button
            variant="contained"
            color="primary"
            sx={{ mr: 2 }}
            startIcon={<AddRoundedIcon />}
            onClick={handleAddStrategy}
          >
            Добавить
          </Button>
        </ProtectedComponent>

        <Button variant="contained" color="primary" sx={{ mr: 2 }} onClick={handleRecalculateDialog}>
          Пересчитать рейтинг
        </Button>

        <Button variant="contained" color="primary" onClick={handleSplitDialog}>
          Сплит
        </Button>
      </div>

      {openStrategyDialog && <StrategyDialog onClose={handleCloseDialog} onUpdate={handleUpdate} mode="create" />}
      {editingStrategyId && (
        <StrategyDialog
          onClose={handleEditDialogClose}
          onUpdate={handleEditDialogUpdate}
          mode="edit"
          strategyId={editingStrategyId}
          notify={(msg) => enqueueSnackbar(msg)}
        />
      )}
      {openRecalculationDialog && <RecalculationDialog onClose={handleCloseDialog} onUpdate={handleUpdate} />}
      {openSplitDialog && <SplitDialog onClose={handleCloseDialog} onUpdate={handleUpdate} />}
      {changeVisibilityCandidate && (
        <VisibilityDialog
          strategyId={changeVisibilityCandidate.strategyId}
          isOpen={changeVisibilityCandidate.isOpen}
          onUpdate={handleUpdate}
          onClose={handleCloseDialog}
        />
      )}
      {deleteCandidate && (
        <DeleteDialog
          strategyId={deleteCandidate.strategyId}
          active={deleteCandidate.active}
          onUpdate={handleUpdate}
          onClose={handleCloseDialog}
        />
      )}

      <TableGridLocal
        name="administration-strategies"
        columns={columns}
        columnFilters={columnsFilters}
        data={strategies}
        totalRowCount={strategies.length}
      />
    </>
  );
};
